/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import CalendarMonthTwoToneIcon from "@mui/icons-material/CalendarMonthTwoTone";
import moment from "moment";
import DatePicker from "react-multi-date-picker";
import DialogLayout from "../../Components/DialogLayout";
import DetailCours, { DetailCoursV2Header } from "../../Components/App/Courses/DetailCoursV2";
import { CourseType, PAST_STATUS_OPTIONS } from "../../config/COURSE_TYPE_CONFIG";
import useTailwindBrand from "../../Theme/useTailwindBrand";
import { capitalizeFirstLetter, months, weekDays } from "../../Utils/Function";
import { DV2 } from "../DeclarationV2/theme";
import { euros, hoursFromHalfHours, monthName, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Avatar, Button, Card, TextLink, fieldStyle, useDv2Theme } from "../DeclarationV2/layout/Ui";
import { getClientDetailPath } from "../Clients/clientDetailNavigation";
import CourseRowActions from "../Courses/CourseRowActions";
import { useListCourse } from "../Courses/useListCourse";
import {
    amountColumnLabel, courseAmount, courseAmountDetail, courseDatesLabel, courseDatesSubline, courseDeposit, courseFormatLabel, courseInitials, courseParentName,
    courseStudentName, courseSubjectLine, groupAmount, groupAmountDetail, groupDeposit, groupHalfHours, groupRange,
    groupRows, groupSessionCount, rangeLabel, withGroupIds,
} from "./courseRows";
import { courseDetailProps, courseStatusNode, statusSummary } from "./courseStatus";
import MontantFactureDialog from "./MontantFactureDialog";
import { ALL_PERIODS, periodBounds } from "./tabs";

export interface CourseTableV2Props {
    type: Exclude<CourseType, "brouillon">;
    /** Onglet Passés : période filtrée, `YYYY-MM` ou `toutes`. */
    period?: string;
    /** Onglet Passés : l'utilisateur a choisi un intervalle libre dans le tableau, la période de la page repasse à « toutes ». */
    onCustomRange?: () => void;
    /** Nombre total de la liste courante, remonté à la page (compteurs et sous-titre). */
    onTotal?: (total: number) => void;
}

const GRID = "minmax(0,1.8fr) minmax(0,1.6fr) minmax(0,0.8fr) minmax(0,1.1fr) minmax(0,1.5fr) 44px";
const PAGE_SIZES = [5, 10, 25];

/**
 * Tableau « Mes cours » repensé (maquettes 6a, 6b, 6c) : mêmes données, même
 * hook `useListCourse` (filtres, pagination, synchronisation Redux, suppression)
 * et même menu d'actions que la liste actuelle, avec les colonnes des maquettes
 * (élève et matière, dates, durée, vous recevez, statut). L'onglet Ce mois
 * regroupe par élève ; un élève qui n'a qu'une ligne s'affiche comme une ligne
 * simple. Le statut garde sa pastille actuelle.
 */
export default function CourseTableV2({ type, period, onCustomRange, onTotal }: CourseTableV2Props) {
    const { primary, fontFamily, text } = useDv2Theme();
    const { classes, brand } = useTailwindBrand();
    const list = useListCourse({ type });
    const {
        teachr, config, page, rowsPerPage,
        nameSearch, setNameSearch, statusSearch, setStatusSearch, dateValues, setDateValues,
        selectedCourse, setSelectedCourse, detailOpen, setDetailOpen,
        expandedGroups, toggleGroup, setMontantOpen,
        isLoading, totalItems, isGrouped, displayedCourses, dataDispatch,
        handleChangePage, handleChangeRowsPerPage, handleOpenDetail, handleSubmitDelete,
    } = list;
    const [periodReady, setPeriodReady] = useState(type !== "historique");
    // Un intervalle choisi à la main dans le tableau ne doit pas être écrasé par le retour de la période à « toutes ».
    const keepCustomRange = useRef(false);

    // Période de l'onglet Passés : traduite en bornes pour le filtre existant du hook.
    useEffect(() => {
        if (type !== "historique") return;
        if (keepCustomRange.current) {
            keepCustomRange.current = false;
        } else if (!period || period === ALL_PERIODS) {
            setDateValues([]);
        } else {
            const { start, end } = periodBounds(period);
            setDateValues([{ toDate: () => moment(start).toDate() }, { toDate: () => moment(end).toDate() }]);
        }
        setPeriodReady(true);
    }, [type, period]);

    const pickRange = (values: any) => {
        const next = Array.isArray(values) ? values : [];
        if (period && period !== ALL_PERIODS) {
            keepCustomRange.current = true;
            onCustomRange?.();
        }
        setDateValues(next);
    };

    useEffect(() => {
        if (!isLoading) onTotal?.(totalItems);
    }, [totalItems, isLoading]);

    const isPast = type === "historique";
    const isUpcoming = type === "avenir";
    const hasCustomRange = isPast && dateValues.length > 0 && (!period || period === ALL_PERIODS);
    const hasFilters = !!(nameSearch || statusSearch || hasCustomRange);
    const clearFilters = () => {
        setNameSearch("");
        setStatusSearch("");
        if (hasCustomRange) setDateValues([]);
        handleChangePage(null, 0);
    };
    const stop = (e: React.MouseEvent) => e.stopPropagation();

    const pageCount = Math.max(1, Math.ceil(totalItems / rowsPerPage));
    const from = totalItems === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(totalItems, (page + 1) * rowsPerPage);
    const pages = (): Array<number | "…"> => {
        if (pageCount <= 6) return Array.from({ length: pageCount }, (_, i) => i);
        const set = new Set<number>([0, 1, 2, page - 1, page, page + 1, pageCount - 1].filter((p) => p >= 0 && p < pageCount));
        const sorted = Array.from(set).sort((a, b) => a - b);
        const result: Array<number | "…"> = [];
        sorted.forEach((p, i) => {
            if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("…");
            result.push(p);
        });
        return result;
    };

    const selectSx = { fontFamily, fontSize: 13, fontWeight: 700, borderRadius: 999, background: "#fff", color: text, "& .MuiOutlinedInput-notchedOutline": { borderColor: DV2.border }, "& .MuiSelect-select": { py: "7px", px: "14px" } };

    // ── Cellules ────────────────────────────────────────────────────────────
    const NameCell = ({ row, subline, size = 40 }: { row: any; subline: string; size?: number }) => {
        const path = row?.clientId ? getClientDetailPath(row.clientId) : null;
        const name = courseStudentName(row);
        const parent = courseParentName(row);
        return (
            <div className="flex items-center gap-3 min-w-0">
                <Avatar text={courseInitials(row)} size={size} />
                <div style={{ minWidth: 0 }}>
                    {path ? (
                        <RouterLink to={path} state={{ fromList: true }} onClick={stop} style={{ color: primary, fontWeight: 800, fontSize: 15, textDecoration: "none", display: "block", lineHeight: 1.25 }}>
                            {name}
                        </RouterLink>
                    ) : (
                        <div style={{ fontWeight: 800, fontSize: 15, lineHeight: 1.25 }}>{name}</div>
                    )}
                    <div style={{ fontSize: 12, color: DV2.muted }}>{subline}</div>
                    {parent && <div style={{ fontSize: 12, color: DV2.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Parent, {parent}</div>}
                </div>
            </div>
        );
    };

    /** Montant mis en avant (prix total TTC chez Verified Trainer, rémunération ailleurs), acompte ou rémunération en dessous. */
    const AmountCell = ({ amount, detail, deposit, note }: { amount: number; detail?: string | null; deposit?: number | null; note?: string | null }) => (
        <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{euros(amount)}</div>
            {deposit != null ? (
                <div style={{ fontSize: 12, color: DV2.muted }}>acompte reçu {euros(deposit, deposit % 1 === 0 ? 0 : 2)}</div>
            ) : detail ? (
                <div style={{ fontSize: 12, color: DV2.muted }}>{detail}{note ? `, ${note}` : ""}</div>
            ) : note ? (
                <div style={{ fontSize: 12, color: DV2.muted }}>{note}</div>
            ) : null}
        </div>
    );

    const rowActions = (c: any, ids: { forChild_id: number; clientId: number; startAt?: any }) => {
        if (isPast) {
            return (
                <Tooltip title="Consulter le détail">
                    <button
                        type="button"
                        onClick={(e) => { stop(e); handleOpenDetail(c); }}
                        aria-label={`Consulter le détail ${vocab.ofTheCourse()}`}
                        style={{ width: 34, height: 34, borderRadius: 10, border: `1px solid ${DV2.border}`, background: "#fff", color: primary, display: "grid", placeItems: "center", cursor: "pointer" }}
                    >
                        <RemoveRedEyeOutlinedIcon fontSize="small" />
                    </button>
                </Tooltip>
            );
        }
        return (
            <div onClick={stop} role="presentation" style={{ display: "inline-flex" }}>
                <CourseRowActions
                    cours={c}
                    teachr={teachr}
                    type={type}
                    dataDispatch={dataDispatch}
                    forChild_id={ids.forChild_id ?? 0}
                    client_id={ids.clientId ?? 0}
                    startAt={ids.startAt}
                    handleSubmitDelete={handleSubmitDelete}
                    config={config}
                />
            </div>
        );
    };

    // ── Ligne simple (À venir, Passés, élève du mois avec une seule ligne) ──
    const FlatRow = ({ course, nameRow, last }: { course: any; nameRow: any; last: boolean }) => {
        const status = courseStatusNode(course, config, classes);
        const actions = rowActions(course, { forChild_id: course?.forChild_id ?? 0, clientId: course?.clientId ?? 0, startAt: course?.startAt });
        const dates = courseDatesLabel(course);
        const datesSub = courseDatesSubline(course, isUpcoming || type === "actif");
        const duration = hoursFromHalfHours(course?.duration);
        return (
            <div style={{ borderBottom: last ? "none" : `1px solid ${DV2.borderSoft}` }}>
                <div className="hidden md:grid items-center" style={{ gridTemplateColumns: GRID, padding: "14px 20px", gap: 12, fontSize: 14 }}>
                    <NameCell row={nameRow} subline={courseSubjectLine(course)} />
                    <div>
                        <div style={{ fontWeight: 700 }}>{dates}</div>
                        <div style={{ fontSize: 12, color: DV2.muted }}>{datesSub}</div>
                    </div>
                    <div style={{ fontWeight: 800 }}>{duration}</div>
                    <AmountCell amount={courseAmount(course)} detail={courseAmountDetail(course)} deposit={courseDeposit(course)} />
                    <div>{status}</div>
                    <div className="flex justify-end">{actions}</div>
                </div>
                <div className="md:hidden flex flex-col gap-3" style={{ padding: "14px 16px" }}>
                    <div className="flex items-start justify-between gap-2">
                        <NameCell row={nameRow} subline={courseSubjectLine(course)} />
                        {actions}
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{dates}</div>
                        <div style={{ fontSize: 12, color: DV2.muted }}>{datesSub}</div>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                        <div style={{ fontSize: 13, color: DV2.muted }}>{duration}</div>
                        <AmountCell amount={courseAmount(course)} detail={courseAmountDetail(course)} deposit={courseDeposit(course)} />
                    </div>
                    <div>{status}</div>
                </div>
            </div>
        );
    };

    // ── Groupe d'un élève (Ce mois) ─────────────────────────────────────────
    const GroupRows = ({ group, index, last }: { group: any; index: number; last: boolean }) => {
        const rows = groupRows(group);
        const open = expandedGroups.has(index);
        const { start, end } = groupRange(group);
        const sessions = groupSessionCount(group);
        const subjects = Array.from(new Set(rows.map((c: any) => capitalizeFirstLetter(c?.subjectAsked ?? "")).filter(Boolean)));
        const subline = `${subjects.length > 0 ? `${subjects.join(", ")}, ` : ""}${plural(rows.length, vocab.course(), vocab.courses())} en ${monthName(moment())}, ${plural(sessions, "séance")}`;
        const summary = statusSummary(rows, config);
        const panelId = `cv2-group-${group?.forChild_id ?? index}`;
        const toggle = (
            <button
                type="button"
                onClick={(e) => { stop(e); toggleGroup(index); }}
                aria-expanded={open}
                aria-controls={panelId}
                aria-label={open ? "Replier" : "Déplier"}
                style={{ width: 34, height: 34, borderRadius: 10, border: open ? "none" : `1px solid ${DV2.border}`, background: open ? primary : "#fff", color: open ? "#fff" : DV2.muted, display: "grid", placeItems: "center", cursor: "pointer", fontSize: 14, fontFamily }}
            >
                {open ? "⌃" : "⌄"}
            </button>
        );
        return (
            <div style={{ borderBottom: last && !open ? "none" : `1px solid ${DV2.borderSoft}` }}>
                <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleGroup(index)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleGroup(index); } }}
                    style={{ background: open ? DV2.tint : undefined, cursor: "pointer" }}
                >
                    <div className="hidden md:grid items-center" style={{ gridTemplateColumns: GRID, padding: "14px 20px", gap: 12, fontSize: 14 }}>
                        <NameCell row={group} subline={subline} />
                        <div>
                            <div style={{ fontWeight: 700 }}>{rangeLabel(start, end)}</div>
                            <div style={{ fontSize: 12, color: DV2.muted }}>{plural(sessions, "séance")}</div>
                        </div>
                        <div style={{ fontWeight: 800 }}>{hoursFromHalfHours(groupHalfHours(group))}</div>
                        <AmountCell amount={groupAmount(group)} detail={groupAmountDetail(group)} deposit={groupDeposit(group)} note="total du mois" />
                        <div style={{ fontSize: 13, color: DV2.muted }}>{summary}</div>
                        <div className="flex justify-end">{toggle}</div>
                    </div>
                    <div className="md:hidden flex flex-col gap-3" style={{ padding: "14px 16px" }}>
                        <div className="flex items-start justify-between gap-2">
                            <NameCell row={group} subline={subline} />
                            {toggle}
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <div style={{ fontSize: 13, color: DV2.muted }}>{hoursFromHalfHours(groupHalfHours(group))}</div>
                            <AmountCell amount={groupAmount(group)} detail={groupAmountDetail(group)} deposit={groupDeposit(group)} note="total du mois" />
                        </div>
                        <div style={{ fontSize: 13, color: DV2.muted }}>{summary}</div>
                    </div>
                </div>
                {open && (
                    <div id={panelId}>
                        {rows.map((raw: any, i: number) => {
                            const c = withGroupIds(raw, group);
                            const status = courseStatusNode(c, config, classes);
                            const actions = rowActions(c, { forChild_id: group?.forChild_id ?? 0, clientId: group?.clientId ?? 0, startAt: group?.startAt });
                            const dates = courseDatesLabel(c);
                            const datesSub = courseDatesSubline(c, false);
                            return (
                                <div key={c?.id ?? i} style={{ background: DV2.pageBg, borderTop: `1px solid ${DV2.borderSoft}` }}>
                                    <div className="hidden md:grid items-center" style={{ gridTemplateColumns: GRID, padding: "12px 20px 12px 72px", gap: 12, fontSize: 14 }}>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: 800, fontSize: 14 }}>{(c?.subjectAsked ?? "").charAt(0).toUpperCase() + (c?.subjectAsked ?? "").slice(1) || vocab.subjectLabel()}</div>
                                            <div style={{ fontSize: 12, color: DV2.muted }}>{courseFormatLabel(c)}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 700 }}>{dates}</div>
                                            <div style={{ fontSize: 12, color: DV2.muted }}>{datesSub}</div>
                                        </div>
                                        <div style={{ fontWeight: 800 }}>{hoursFromHalfHours(c?.duration)}</div>
                                        <AmountCell amount={courseAmount(c)} detail={courseAmountDetail(c)} deposit={courseDeposit(c)} />
                                        <div>{status}</div>
                                        <div className="flex justify-end">{actions}</div>
                                    </div>
                                    <div className="md:hidden flex flex-col gap-2" style={{ padding: "12px 16px 12px 28px" }}>
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <div style={{ fontWeight: 800, fontSize: 14 }}>{(c?.subjectAsked ?? "").charAt(0).toUpperCase() + (c?.subjectAsked ?? "").slice(1)}</div>
                                                <div style={{ fontSize: 12, color: DV2.muted }}>{courseFormatLabel(c)}</div>
                                            </div>
                                            {actions}
                                        </div>
                                        <div style={{ fontWeight: 700, fontSize: 14 }}>{dates}</div>
                                        <div style={{ fontSize: 12, color: DV2.muted }}>{datesSub}</div>
                                        <div className="flex items-center justify-between gap-3">
                                            <div style={{ fontSize: 13, color: DV2.muted }}>{hoursFromHalfHours(c?.duration)}</div>
                                            <AmountCell amount={courseAmount(c)} detail={courseAmountDetail(c)} deposit={courseDeposit(c)} />
                                        </div>
                                        <div>{status}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const showLoading = isLoading || !periodReady;
    const rows: any[] = displayedCourses ?? [];

    // Passés : séparateur par mois (comme le tableau des documents, qui sépare par jour).
    // L'API trie par date de début décroissante, les cours d'un même mois se suivent.
    const monthSections = (): Array<{ key: string; heading: string; items: any[] }> => {
        const out: Array<{ key: string; heading: string; items: any[] }> = [];
        rows.forEach((item) => {
            const d = item?.startDatetime ? moment(item.startDatetime) : null;
            const key = d && d.isValid() ? d.format("YYYY-MM") : "unknown";
            const last = out[out.length - 1];
            if (last && last.key === key) last.items.push(item);
            else out.push({ key, heading: key === "unknown" ? "Date inconnue" : `${capitalizeFirstLetter(monthName(d))} ${d!.format("YYYY")}`, items: [item] });
        });
        return out;
    };

    return (
        <>
            <Card padding={0} style={{ overflow: "hidden", fontFamily, color: text }}>
                {/* Barre de filtres */}
                <div className="flex flex-col lg:flex-row lg:items-center gap-3" style={{ padding: "14px 20px", borderBottom: `1px solid ${DV2.borderSoft}` }}>
                    <input
                        type="search"
                        value={nameSearch}
                        onChange={(e) => { setNameSearch(e.target.value); handleChangePage(null, 0); }}
                        placeholder={`Filtrer par ${vocab.client()}`}
                        aria-label={`Filtrer les ${vocab.courses()} par ${vocab.client()}`}
                        style={{ ...fieldStyle(), background: DV2.pageBg, fontWeight: 500, fontSize: 14, maxWidth: 460 }}
                    />
                    <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
                        {isPast && (
                            <div
                                className="flex items-center gap-2"
                                style={{ background: "#fff", border: `1px solid ${DV2.border}`, borderRadius: 999, padding: "4px 12px", fontSize: 13, fontFamily }}
                                aria-label="Filtrer par période"
                            >
                                <CalendarMonthTwoToneIcon sx={{ color: DV2.muted, fontSize: 18, flexShrink: 0 }} />
                                <DatePicker
                                    inputClass="custom-input"
                                    placeholder="Filtrer par période"
                                    containerStyle={{ width: 190, border: "none" }}
                                    style={{ border: "none", boxShadow: "none", fontFamily, fontSize: 13, fontWeight: 700, color: text, background: "transparent", width: "100%", padding: "4px 0" }}
                                    months={months}
                                    weekDays={weekDays}
                                    format="DD/MM/YYYY"
                                    value={dateValues}
                                    onChange={pickRange}
                                    range
                                    rangeHover
                                    portal
                                    className={brand === "verified-trainer" ? "teal" : ""}
                                />
                                {hasCustomRange && (
                                    <button type="button" onClick={() => pickRange([])} aria-label="Retirer le filtre par période" style={{ border: "none", background: "none", color: DV2.muted, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
                                )}
                            </div>
                        )}
                        {isPast && (
                            <FormControl size="small">
                                <Select
                                    displayEmpty
                                    value={statusSearch}
                                    onChange={(e) => { setStatusSearch(e.target.value); handleChangePage(null, 0); }}
                                    renderValue={(selected) => (selected ? PAST_STATUS_OPTIONS.find((o) => o.value === selected)?.label ?? "Statut" : "Statut")}
                                    sx={selectSx}
                                    inputProps={{ "aria-label": "Filtrer par statut" }}
                                >
                                    {PAST_STATUS_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily, fontSize: 13 }}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {isPast && totalItems > 0 && <Button label="Total facturé" variant="secondary" size="sm" onClick={() => setMontantOpen(true)} />}
                        {hasFilters && <TextLink label="Effacer les filtres" onClick={clearFilters} />}
                    </div>
                </div>

                {/* En-tête (web) */}
                <div className="hidden md:grid" style={{ gridTemplateColumns: GRID, padding: "10px 20px", fontSize: 11, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".06em", borderBottom: `1px solid ${DV2.borderSoft}`, gap: 12 }}>
                    <span>{vocab.client()} et {vocab.subjectLabelLower()}</span>
                    <span>Dates</span>
                    <span>Durée</span>
                    <span>{amountColumnLabel()}</span>
                    <span>Statut</span>
                    <span />
                </div>

                {showLoading ? (
                    <div aria-busy="true" aria-label="Chargement">
                        {[...Array(4)].map((_, i) => (
                            <div key={i} className="flex gap-3" style={{ padding: "18px 20px", borderBottom: i < 3 ? `1px solid ${DV2.borderSoft}` : "none" }}>
                                {[...Array(5)].map((_, j) => (
                                    <div key={j} style={{ flex: 1, height: 16, borderRadius: 6, background: DV2.borderSoft }} />
                                ))}
                            </div>
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <div style={{ padding: "48px 20px", textAlign: "center", color: DV2.muted, fontSize: 14 }}>
                        <div style={{ fontSize: 28 }} aria-hidden>{config.emptyEmoji}</div>
                        <div style={{ marginTop: 8 }}>{hasFilters ? `Aucun ${vocab.course()} ne correspond à vos filtres.` : config.emptyLabel}</div>
                        {hasFilters && <div style={{ marginTop: 8 }}><TextLink label="Effacer les filtres" onClick={clearFilters} /></div>}
                    </div>
                ) : isPast && !isGrouped ? (
                    monthSections().map((section, s, sections) => (
                        <section key={section.key} aria-label={section.heading}>
                            <div style={{ padding: "8px 20px", fontSize: 11, fontWeight: 800, color: DV2.placeholder, textTransform: "uppercase", letterSpacing: ".06em", background: "#F9FBFD", borderBottom: `1px solid ${DV2.borderSoft}` }}>{section.heading}</div>
                            {section.items.map((item: any, index: number) => (
                                <FlatRow key={item?.id ?? index} course={item} nameRow={item} last={index === section.items.length - 1 && s === sections.length - 1} />
                            ))}
                        </section>
                    ))
                ) : (
                    rows.map((item: any, index: number) => {
                        const last = index === rows.length - 1;
                        return isGrouped
                            ? <GroupRows key={item?.forChild_id ?? item?.clientId ?? index} group={item} index={index} last={last} />
                            : <FlatRow key={item?.id ?? index} course={item} nameRow={item} last={last} />;
                    })
                )}

                {/* Pied du tableau */}
                {!showLoading && totalItems > 0 && (
                    <div style={{ borderTop: `1px solid ${DV2.borderSoft}` }}>
                        {isUpcoming && (
                            <div style={{ padding: "12px 20px 0", fontSize: 13, color: DV2.muted }}>
                                Ces {vocab.courses()} peuvent être modifié{vocab.course() === "prestation" ? "e" : ""}s ou annulé{vocab.course() === "prestation" ? "e" : ""}s jusqu'à leur date de début.
                            </div>
                        )}
                        <div className="flex flex-wrap items-center justify-between gap-3" style={{ padding: "14px 20px" }}>
                            <span style={{ fontSize: 13, color: DV2.muted }}>
                                {isGrouped ? `${plural(totalItems, vocab.client(), vocab.clients())}, ${from} à ${to}` : `${from} à ${to} sur ${totalItems}`}
                            </span>
                            <div className="flex items-center gap-2">
                                <label style={{ fontSize: 12, color: DV2.muted }} className="hidden sm:flex items-center gap-2">
                                    Par page
                                    <select
                                        value={rowsPerPage}
                                        onChange={(e) => handleChangeRowsPerPage({ target: { value: e.target.value } } as any)}
                                        style={{ ...fieldStyle(), width: "auto", padding: "4px 8px", fontSize: 12, fontFamily, appearance: "auto" as any }}
                                    >
                                        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </label>
                                <nav aria-label="Pagination" className="flex items-center gap-1">
                                    {pages().map((p, i) => p === "…" ? (
                                        <span key={`gap-${i}`} style={{ width: 32, textAlign: "center", color: DV2.placeholder }}>…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            type="button"
                                            onClick={() => handleChangePage(null, p)}
                                            aria-current={p === page ? "page" : undefined}
                                            style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${p === page ? primary : DV2.border}`, background: p === page ? primary : "#fff", color: p === page ? "#fff" : text, fontWeight: 700, fontFamily, cursor: "pointer", fontSize: 13 }}
                                        >
                                            {p + 1}
                                        </button>
                                    ))}
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* Montant total facturé (Passés), même fenêtre qu'aujourd'hui */}
            {isPast && <MontantFactureDialog {...list} />}

            {/* Fiche du cours (Passés) : l'en-tête de la fiche tient lieu de titre */}
            {isPast && selectedCourse && (() => {
                const props = courseDetailProps(selectedCourse, teachr, config, classes);
                const close = () => { setDetailOpen(false); setSelectedCourse(null); };
                return (
                    <DialogLayout
                        title={<DetailCoursV2Header {...props} />}
                        open={detailOpen}
                        maxWidth="md"
                        handleClose={close}
                        customDialogActions="hidden"
                        handleCloseIcon={close}
                    >
                        <DetailCours {...props} />
                    </DialogLayout>
                );
            })()}
        </>
    );
}
